/**
 * Modal Scroll Fix Utility
 * Ensures modal scrolling works properly with auto-scaling
 */

class ModalScrollFix {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupModalObserver());
    } else {
      this.setupModalObserver();
    }
  }

  setupModalObserver() {
    // Create a MutationObserver to watch for modal creation
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if it's a modal container
            const modalContainers = node.querySelectorAll 
              ? node.querySelectorAll('.modal-container, .blog-modal-container, .project-modal-container, .blog-modal-scroll-container')
              : [];
            
            // Also check if the node itself is a modal container
            if (node.classList && (
              node.classList.contains('modal-container') ||
              node.classList.contains('blog-modal-container') ||
              node.classList.contains('project-modal-container') ||
              node.classList.contains('blog-modal-scroll-container')
            )) {
              this.fixModalScrolling(node);
            }

            // Fix any modal containers found within the added node
            modalContainers.forEach(container => this.fixModalScrolling(container));
          }
        });
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also fix any existing modals
    this.fixExistingModals();
  }

  fixExistingModals() {
    const existingModals = document.querySelectorAll('.modal-container, .blog-modal-container, .project-modal-container, .blog-modal-scroll-container');
    existingModals.forEach(modal => this.fixModalScrolling(modal));
  }

  fixModalScrolling(modalContainer) {
    if (!modalContainer) return;

    // Get current scale
    const computedStyle = getComputedStyle(document.documentElement);
    const currentScale = parseFloat(computedStyle.getPropertyValue('--auto-scale')) || 1;

    // Apply scroll fixes
    modalContainer.style.overflowY = 'auto';
    modalContainer.style.overflowX = 'hidden';
    modalContainer.style.webkitOverflowScrolling = 'touch';
    modalContainer.style.scrollbarWidth = 'thin';
    modalContainer.style.scrollbarColor = '#105652 #f1f1f1';
    
    // Adjust max height based on scale
    // Try to get adjusted height from CSS variable first
    const vhAdjusted = computedStyle.getPropertyValue('--vh-adjusted').trim();
    
    if (vhAdjusted && vhAdjusted.includes('calc')) {
      modalContainer.style.maxHeight = `calc(${vhAdjusted} - 100px)`;
    } else {
      const adjustedHeight = Math.min(window.innerHeight - 40, (window.innerHeight - 40) / currentScale);
      modalContainer.style.maxHeight = `${adjustedHeight}px`;
    }

    // Force hardware acceleration for smooth scrolling
    modalContainer.style.transform = 'translateZ(0)';
    modalContainer.style.willChange = 'scroll-position';

    // Add touch scroll behavior for mobile
    modalContainer.style.overscrollBehavior = 'contain';

    console.log(`🔧 Modal scroll fixed for scale ${Math.round(currentScale * 100)}%`);
    console.log(`   Max height: ${adjustedHeight}px`);

    // Add scroll event listener to ensure smooth scrolling
    this.addSmoothScrolling(modalContainer);
  }

  addSmoothScrolling(modalContainer) {
    let isScrolling = false;

    modalContainer.addEventListener('scroll', () => {
      if (!isScrolling) {
        window.requestAnimationFrame(() => {
          // Smooth scrolling frame
          isScrolling = false;
        });
        isScrolling = true;
      }
    }, { passive: true });

    // Handle wheel events for better scrolling
    modalContainer.addEventListener('wheel', (e) => {
      // Allow native scrolling but ensure it's smooth
      modalContainer.style.scrollBehavior = 'smooth';
      
      setTimeout(() => {
        modalContainer.style.scrollBehavior = 'auto';
      }, 100);
    }, { passive: true });
  }

  // Manual fix method
  fixAllModals() {
    this.fixExistingModals();
    console.log('🔧 All modal scrolling fixed manually');
  }
}

// Create and export singleton instance
const modalScrollFix = new ModalScrollFix();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.modalScrollFix = modalScrollFix;
}

export default modalScrollFix;